import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import complianceService from '../../../services/complianceService';

const EmailComplianceSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [physicalAddress, setPhysicalAddress] = useState<string>('');
  const [preferenceCenterUrl, setPreferenceCenterUrl] = useState<string>('');
  const [dmarcConfigured, setDmarcConfigured] = useState<boolean>(false);
  const [bimiConfigured, setBimiConfigured] = useState<boolean>(false);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const [statusRes, settingsRes] = await Promise.all([
          complianceService.getStatus(),
          complianceService.getSettings(),
        ]);
        setDmarcConfigured(!!statusRes?.status?.dmarc?.configured);
        setBimiConfigured(!!statusRes?.status?.bimi?.configured);
        const s = settingsRes?.settings;
        if (s) {
          setPhysicalAddress(s.physical_address || '');
          setPreferenceCenterUrl(s.preference_center_url || '');
        }
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load compliance settings');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Basic URL validation
      if (preferenceCenterUrl && !/^https?:\/\//i.test(preferenceCenterUrl)) {
        toast.error('Preference center URL must start with http(s)');
        setSaving(false);
        return;
      }
      await complianceService.updateSettings({
        physical_address: physicalAddress || null,
        preference_center_url: preferenceCenterUrl || null,
      });
      toast.success('Email compliance settings saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-medium">Email Compliance</h2>
        <p className="text-sm text-gray-600">Configure footer details and preference center for CAN-SPAM/GDPR compliance.</p>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded border p-4">
              <div className="font-semibold mb-2">DMARC</div>
              <div className={`inline-flex items-center px-2 py-1 rounded text-sm ${dmarcConfigured ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                {dmarcConfigured ? 'Configured' : 'Not configured'}
              </div>
              <p className="text-xs text-gray-500 mt-2">DNS verification will be added later. Configure DMARC on your sending domains for better deliverability.</p>
            </div>
            <div className="bg-white rounded border p-4">
              <div className="font-semibold mb-2">BIMI</div>
              <div className={`inline-flex items-center px-2 py-1 rounded text-sm ${bimiConfigured ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                {bimiConfigured ? 'Configured' : 'Not configured'}
              </div>
              <p className="text-xs text-gray-500 mt-2">BIMI indicates brand authenticity in inboxes. DNS checks will be added later.</p>
            </div>
          </div>

          <div className="bg-white rounded border p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={4}
                value={physicalAddress}
                onChange={(e) => setPhysicalAddress(e.target.value)}
                placeholder="Company Name\n123 Main St\nCity, State, ZIP\nCountry"
              />
              <p className="text-xs text-gray-500 mt-1">Shown in email footer if provided.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preference Center URL</label>
              <input
                className="w-full border rounded px-3 py-2"
                value={preferenceCenterUrl}
                onChange={(e) => setPreferenceCenterUrl(e.target.value)}
                placeholder="https://example.com/email-preferences"
              />
              <p className="text-xs text-gray-500 mt-1">Used for managing subscriber preferences. A per-contact unsubscribe link is still included.</p>
            </div>

            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailComplianceSettings;
